import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Banknote,
  Building2,
  Calendar,
  FileText,
  HeadphonesIcon,
  Info,
  Palette,
  Settings,
  Shield,
} from "lucide-react";

import { Cargando, ErrorCarga, PageHeader } from "../../components/ui";
import { fetchEmpresaConfiguracion } from "./api";
import { AparienciaTab } from "./sections/AparienciaTab";
import { AtencionTab } from "./sections/AtencionTab";
import { BancoTab } from "./sections/BancoTab";
import { BoletaTab } from "./sections/BoletaTab";
import { CicloTab } from "./sections/CicloTab";
import { IdentificacionTab } from "./sections/IdentificacionTab";
import { SissTab } from "./sections/SissTab";

type TabId =
  | "identificacion"
  | "banco"
  | "atencion"
  | "boleta"
  | "ciclo"
  | "siss"
  | "apariencia";

const TABS: {
  id: TabId;
  titulo: string;
  icon: typeof Building2;
}[] = [
  { id: "identificacion", titulo: "Identificación", icon: Building2 },
  { id: "banco", titulo: "Banco", icon: Banknote },
  { id: "atencion", titulo: "Atención al usuario", icon: HeadphonesIcon },
  { id: "boleta", titulo: "Boleta", icon: FileText },
  { id: "ciclo", titulo: "Calendario del ciclo", icon: Calendar },
  { id: "siss", titulo: "Clasificación SISS", icon: Shield },
  { id: "apariencia", titulo: "Apariencia", icon: Palette },
];

function esTab(v: string | null): v is TabId {
  return TABS.some((t) => t.id === v);
}

export function ConfiguracionPage(): JSX.Element {
  // La pestaña vive en la URL (`?tab=boleta`): F5, atrás y los enlaces
  // directos caen en la misma sección (NAV-09).
  const [params, setParams] = useSearchParams();
  const crudo = params.get("tab");
  const tab: TabId = esTab(crudo) ? crudo : "identificacion";
  const setTab = (id: TabId): void => {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  // El cfg general lo carga la página y se pasa a las tabs que lo necesitan.
  // CicloTab usa su propio endpoint y query key separado.
  const cfgQ = useQuery({
    queryKey: ["empresa", "configuracion"],
    queryFn: fetchEmpresaConfiguracion,
  });

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <PageHeader
          title="Configuración"
          icon={Settings}
          subtitle="Datos del comité que salen en la boleta, el calendario del ciclo y la clasificación SISS."
        />
        {/* NAV-14: las cuentas de staff son del operador SaaS (CLAUDE.md,
            recorrido T8); el comité no tiene pantalla de usuarios. */}
        <div
          role="note"
          className="flex items-start gap-2 rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-700 ring-1 ring-neutral-200"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" aria-hidden="true" />
          <span>
            Las cuentas de usuario las administra InnovaClarke: escríbenos para
            altas, bajas o cambios de clave.
          </span>
        </div>
      </div>

      {/* Tabs nav */}
      <div className="border-b border-neutral-200">
        <div role="tablist" className="flex flex-wrap gap-1 -mb-px" aria-label="Secciones de la configuración">
          {TABS.map((t) => {
            const activa = t.id === tab;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`config-tab-${t.id}`}
                aria-selected={activa}
                aria-controls="config-panel"
                onClick={() => setTab(t.id)}
                className={
                  activa
                    ? "inline-flex items-center gap-2 border-b-2 border-brand-600 px-3 py-2 text-sm font-semibold text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    : "inline-flex items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm text-neutral-600 hover:text-neutral-900 hover:border-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                }
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                {t.titulo}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div
        className="pt-2"
        id="config-panel"
        role="tabpanel"
        aria-labelledby={`config-tab-${tab}`}
      >
        {tab === "ciclo" ? (
          <CicloTab />
        ) : tab === "apariencia" ? (
          <AparienciaTab />
        ) : cfgQ.isPending ? (
          <Cargando texto="Cargando configuración…" />
        ) : cfgQ.isError || !cfgQ.data ? (
          <ErrorCarga
            error={cfgQ.error}
            mensaje="No se pudo cargar la configuración"
            onReintentar={() => void cfgQ.refetch()}
          />
        ) : tab === "identificacion" ? (
          <IdentificacionTab cfg={cfgQ.data} />
        ) : tab === "banco" ? (
          <BancoTab cfg={cfgQ.data} />
        ) : tab === "atencion" ? (
          <AtencionTab cfg={cfgQ.data} />
        ) : tab === "boleta" ? (
          <BoletaTab cfg={cfgQ.data} />
        ) : tab === "siss" ? (
          <SissTab cfg={cfgQ.data} />
        ) : null}
      </div>
    </div>
  );
}
